import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AssetsSidebar } from "./AssetsSidebar";
import { useSidebarContext } from "../SidebarContext";
import { useFetchAssets } from "./hooks/useFetchAssets"; // Import the custom hook
import { Optimize3DModelDialog } from "./Optimize3DModelDialog";
import configs from "../../utils/configs";
import { proxiedUrlFor } from "../../utils/media-url-utils";
import buildAssetUrl from "../../utils/build-asset-url";
import PropTypes from "prop-types";
import { useArtworks } from "./hooks/useArtworks";
import { useUploadFormContext } from "../contexts/UploadFormContext";
import { UPLOAD_DRAWER_TOGGLER_ID } from "../contexts/ToolbarContext";
import { useWorkspaceContext } from "../contexts/WorkspaceContext";
import { getAuthHeaders } from "../../utils/getToken";
import { useModalContext } from "./ModalContext";
import { ProgressDialog } from "../modal/ProgressDialog";
import { useIntl } from "react-intl";
import {
  buildAcquirePayload,
  canAddLibraryAsset,
  isStorageLimitError,
  extractArtworkMetadata,
  shouldAcquireBeforePlace,
  resolveAcquireUrl
} from "./utils/library-acquire-utils";
import { buildExternalLinkMediaOptions, mergeMediaOptions } from "./utils/asset-spawn-utils";
import notificationsCenter from "../../singleton/NotificationsCenter";

const ASSETS_DRAWER_EXIT_MS = 250;

const normalizeBaseUrl = baseUrl => String(baseUrl || "").replace(/\/+$/, "");

const buildPersistencePublicArtUrl = (asset, configs) => {
  const museum = asset?.artwork?.museum?.toLowerCase();
  const artworkId = asset?.artwork?.id ?? asset?.metadata?.artwork?.id ?? asset?.id ?? null;
  const persistenceBaseUrl = normalizeBaseUrl(configs?.PERSISTENCE_API_URL);

  if (!museum || !artworkId || !persistenceBaseUrl) {
    return null;
  }

  return `${persistenceBaseUrl}/integrations/public-art/${museum}/${encodeURIComponent(artworkId)}`;
};

export function AssetsSidebarContainer({ scene, signedIn, initialTab = null, userName, sessionId }) {
  const { setSidebar } = useSidebarContext();
  const { selectedWorkspace } = useWorkspaceContext();
  const { showNonHistoriedDialog, closeDialog } = useModalContext();
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(true);
  const closeTimeoutRef = useRef(null);

  // Build assetsViewEndpoint with workspace in the path
  const assetsViewEndpoint = useMemo(() => {
    const baseEndpoint = configs.PERSISTENCE_API_URL + "/assets-view";
    if (selectedWorkspace?.id) {
      return `${configs.PERSISTENCE_API_URL}/workspaces/${selectedWorkspace.id}/assets-view`;
    }
    return baseEndpoint;
  }, [selectedWorkspace]);

  // Build assetsEndpoint with workspace in the path
  const assetsEndpoint = useMemo(() => {
    const baseEndpoint = configs.PERSISTENCE_API_URL + "/assets";
    if (selectedWorkspace?.id) {
      return `${configs.PERSISTENCE_API_URL}/workspaces/${selectedWorkspace.id}/assets`;
    }
    return baseEndpoint;
  }, [selectedWorkspace]);

  const {
    assets,
    loading,
    error,
    searchQuery,
    setSearchQuery,
    fileTypeFilter,
    handleFileTypeFilterChange,
    fileTypeOptions,
    assetCategoryFilter,
    handleAssetCategoryFilterChange,
    assetCategoryOptions,
    renameAsset,
    deleteAsset,
    tabs,
    selectedTab,
    handleSelectedTabChange,
    sourceOptions,
    handleSourceChange,
    source,
    hasNext,
    hasPrev,
    onNext,
    onPrev,
    editable,
    onChangePlaceLoading,
    placeLoading,
    upsertAsset,
    refetch
  } = useFetchAssets({
    assetsViewEndpoint,
    assetsEndpoint,
    publicDomainArtEndpoint: `${normalizeBaseUrl(configs.PERSISTENCE_API_URL)}/integrations/public-art`,
    librariesApiKey: configs.SKETCHFAB_API_KEY,
    defaultToken: null,
    signedIn
  }); // Use the custom hook

  const { getPublicDomainArtwork } = useArtworks();
  const { initEditMode, onClear } = useUploadFormContext();

  const [assetToOptimize, setAssetToOptimize] = useState(null);
  const [isOptimizeDialogOpen, setIsOptimizeDialogOpen] = useState(false);
  const dialogAsset = useMemo(() => {
    if (!assetToOptimize) {
      return null;
    }

    const collectionId = assetToOptimize.collectionId ?? assetToOptimize.collection_id ?? null;
    return {
      ...assetToOptimize,
      collectionId
    };
  }, [assetToOptimize]);

  // Helper function to open file upload drawer via window events
  const openFileUploadDrawer = () => {
    window.dispatchEvent(new CustomEvent(`open-${UPLOAD_DRAWER_TOGGLER_ID}`));
  };

  const closeAssetsSidebar = useCallback(() => {
    setIsOpen(false);

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => setSidebar(""), ASSETS_DRAWER_EXIT_MS);
  }, [setSidebar]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (initialTab) {
      handleSelectedTabChange(initialTab);
    }
  }, [initialTab, handleSelectedTabChange]);

  const handleOptimizeAsset = (event, asset) => {
    event?.stopPropagation?.();
    if (!asset) {
      return;
    }
    setAssetToOptimize(asset);
    setIsOptimizeDialogOpen(true);
  };

  const handleCloseOptimizeDialog = () => {
    setIsOptimizeDialogOpen(false);
    setAssetToOptimize(null);
  };

  const handleOptimizationComplete = (updatedAsset, meta = {}) => {
    if (updatedAsset) {
      upsertAsset(updatedAsset, { prepend: meta?.isCopy });
    }
    refetch();
    handleCloseOptimizeDialog();
  };

  const processThumbnailUrl = thumbnailUrl => {
    return proxiedUrlFor(thumbnailUrl);
  };

  const proxyUrl = useCallback(
    url => {
      if (selectedTab === "public-domain-art") {
        return proxiedUrlFor(url);
      }

      return url;
    },
    [selectedTab]
  );

  const isPublicDomainArt = asset => {
    const museum = asset?.artwork?.museum?.toLowerCase();
    return museum === "nga" || museum === "met";
  };

  const showAcquireProgressDialog = useCallback(() => {
    showNonHistoriedDialog(ProgressDialog, {
      title: intl.formatMessage({
        id: "assets.acquire.progress.title",
        defaultMessage: "Copying asset"
      }),
      message: intl.formatMessage({
        id: "assets.acquire.progress.message",
        defaultMessage: "Copying to library..."
      }),
      cancelable: true,
      onCancel: closeDialog
    });
  }, [showNonHistoriedDialog, closeDialog, intl]);

  const acquireEndpoint = useMemo(() => {
    if (selectedWorkspace?.id) {
      return `${configs.PERSISTENCE_API_URL}/workspaces/${selectedWorkspace.id}/assets/acquire`;
    }
    return `${configs.PERSISTENCE_API_URL}/assets/acquire`;
  }, [selectedWorkspace]);

  const acquireLibraryAsset = useCallback(
    async (asset, { forceDuplicate = false, sourceTab = null } = {}) => {
      const authHeaders = await getAuthHeaders(acquireEndpoint, "POST");
      if (!authHeaders.Authorization) {
        const error = new Error("No token");
        error.status = 401;
        throw error;
      }

      const response = await fetch(acquireEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders
        },
        body: JSON.stringify(
          buildAcquirePayload({
            asset,
            forceDuplicate,
            selectedTab: sourceTab || selectedTab,
            urlOverride: await resolveAcquireUrl({
              asset,
              selectedTab: sourceTab || selectedTab
            })
          })
        )
      });

      if (!response.ok) {
        const error = new Error("Failed to acquire asset");
        error.status = response.status;
        throw error;
      }

      const data = await response.json();
      return data?.data || data;
    },
    [acquireEndpoint, selectedTab]
  );

  const buildArtworkMediaOptions = useCallback(asset => {
    const artworkData = extractArtworkMetadata(asset);
    const museum = artworkData?.museum?.toLowerCase();
    const isPublicMuseum = museum === "nga" || museum === "met";

    if (!artworkData || !isPublicMuseum) {
      return undefined;
    }

    const transformedArtworkData = {
      ...artworkData,
      frame: artworkData.metadata?.frame?.model || artworkData.metadata?.frame?.url || null,
      mediumName: artworkData.metadata?.medium?.name || artworkData.medium || "Digital File",
      glbModel: artworkData.metadata?.medium?.model || null
    };

    return {
      artworkInfo: JSON.stringify(transformedArtworkData),
      isArtwork: true
    };
  }, []);

  const buildAssetMediaOptions = useCallback(
    asset => mergeMediaOptions(buildArtworkMediaOptions(asset), buildExternalLinkMediaOptions(asset)),
    [buildArtworkMediaOptions]
  );

  const spawnAssetsInScene = useCallback(
    assetsToSpawn => {
      assetsToSpawn.forEach((asset, index) => {
        const mediaOptions = buildAssetMediaOptions(asset);
        const finalUrl = asset?.uuid ? buildAssetUrl(asset, configs) : asset?.url;
        const entry = {
          ...asset,
          type: "media",
          url: finalUrl,
          thumbnail: proxyUrl(asset?.thumbnail),
          index,
          mediaOptions
        };

        if (asset?.uuid) {
          entry.assetSrc = finalUrl;
        }

        scene.emit("action_selected_media_result_entry", {
          entry,
          selectAction: "spawn"
        });
      });
    },
    [scene, proxyUrl, buildAssetMediaOptions]
  );

  const dispatchObjectPlacedMessage = useCallback(() => {
    setTimeout(() => {
      window.APP.messageDispatch.push(
        {
          sender: userName,
          senderSessionId: sessionId
        },
        "object"
      );
    }, 1000);
  }, [userName, sessionId]);

  const handleAddToLibrary = useCallback(
    async asset => {
      let storageLimitReached = false;

      showAcquireProgressDialog();
      try {
        await acquireLibraryAsset(asset, { forceDuplicate: true, sourceTab: selectedTab });
        notificationsCenter.success("Asset added to library");
      } catch (error) {
        if (isStorageLimitError(error)) {
          storageLimitReached = true;
          scene.emit("storage_limit_reached");
        } else {
          console.error("Error acquiring library asset:", error);
        }
      } finally {
        if (!storageLimitReached) {
          closeDialog();
        }
      }
    },
    [showAcquireProgressDialog, acquireLibraryAsset, scene, closeDialog, selectedTab]
  );

  const onSelectAssets = async assets => {
    onChangePlaceLoading(true);

    if (shouldAcquireBeforePlace(selectedTab)) {
      let storageLimitReached = false;

      showAcquireProgressDialog();
      try {
        const results = await Promise.allSettled(
          assets.map(asset => acquireLibraryAsset(asset, { forceDuplicate: false, sourceTab: selectedTab }))
        );
        const acquiredAssets = [];
        results.forEach(result => {
          if (result.status === "fulfilled") {
            acquiredAssets.push(result.value);
          } else if (isStorageLimitError(result.reason)) {
            if (!storageLimitReached) {
              storageLimitReached = true;
              scene.emit("storage_limit_reached");
            }
          } else {
            console.error("Error acquiring library asset:", result.reason);
          }
        });

        if (acquiredAssets.length > 0) {
          if (selectedTab === "public-domain-art") {
            acquiredAssets.forEach(acquiredAsset => {
              if (!acquiredAsset?.metadata?.artwork) {
                console.warn(
                  "Acquire response is missing metadata.artwork for public-domain-art asset:",
                  acquiredAsset?.id || acquiredAsset?.uuid || acquiredAsset?.title || acquiredAsset?.url
                );
              }
            });
          }

          spawnAssetsInScene(acquiredAssets);
          dispatchObjectPlacedMessage();
          notificationsCenter.success(
            acquiredAssets.length > 1 ? "Assets copied and placed" : "Asset copied and placed"
          );
        }
      } finally {
        if (!storageLimitReached) {
          closeDialog();
        }
        onChangePlaceLoading(false);
      }

      return;
    }

    // Create an array of promises to handle URL generation
    const urlPromises = assets.map(async asset => {
      let urlForProxy =
        asset?.artwork && asset?.artwork?.artwork_model ? asset?.artwork?.artwork_model : proxyUrl(asset.url);
      let assetSrcForPersistence = null;
      if (asset?.artwork && !asset?.artwork?.artwork_model) {
        let publicArtUrl = null;
        if (isPublicDomainArt(asset)) {
          publicArtUrl = buildPersistencePublicArtUrl(asset, configs);
        }

        if (publicArtUrl) {
          urlForProxy = publicArtUrl;
          assetSrcForPersistence = publicArtUrl;
        } else {
          const artwork = await getPublicDomainArtwork(asset);
          if (artwork?.artwork_model) {
            urlForProxy = artwork?.artwork_model;
          }
        }
      }

      return { asset, urlForProxy, assetSrcForPersistence };
    });

    // Wait for all URLs to be generated
    const assetsWithUrls = await Promise.all(urlPromises);

    // Proceed with the forEach loop
    assetsWithUrls.forEach(({ asset, urlForProxy, assetSrcForPersistence }, index) => {
      const mediaOptions = buildAssetMediaOptions(asset);

      // For "files" tab with UUID, use /assets/{uuid} URL to enable real-time metadata consumption
      const finalUrl = selectedTab === "files" && asset.uuid ? buildAssetUrl(asset, configs) : urlForProxy;

      const entry = {
        ...asset,
        type: "media",
        url: finalUrl,
        thumbnail: proxyUrl(asset.thumbnail),
        index,
        mediaOptions
      };

      // Also set assetSrc as backup for persistence priority
      if (selectedTab === "files" && asset.uuid) {
        entry.assetSrc = finalUrl;
      } else if (assetSrcForPersistence) {
        entry.assetSrc = assetSrcForPersistence;
      }

      scene.emit("action_selected_media_result_entry", {
        entry,
        selectAction: "spawn"
      });
    });

    onChangePlaceLoading(false);

    // Send a delayed message to the user that the asset was placed
    dispatchObjectPlacedMessage();
  };

  const canShowAddToLibrary = useMemo(
    () => canAddLibraryAsset({ selectedTab, workspace: selectedWorkspace }),
    [selectedTab, selectedWorkspace]
  );
  const canUpdateAssets = Boolean(
    selectedWorkspace?.authorization?.projection_status === "ready" &&
      selectedWorkspace.authorization.permissions?.includes("asset:update")
  );
  const canDeleteAssets = Boolean(
    selectedWorkspace?.authorization?.projection_status === "ready" &&
      selectedWorkspace.authorization.permissions?.includes("asset:delete")
  );
  const canViewAssets = Boolean(
    selectedWorkspace?.authorization?.projection_status === "ready" &&
      selectedWorkspace.authorization.permissions?.includes("workspace:read") &&
      selectedWorkspace.authorization.permissions?.includes("asset:read")
  );

  const selectionDisabled = !canViewAssets || (!canShowAddToLibrary && shouldAcquireBeforePlace(selectedTab));

  return (
    <>
      <AssetsSidebar
        items={assets}
        loading={loading}
        error={error}
        open={isOpen}
        onClose={closeAssetsSidebar}
        processThumbnailUrl={processThumbnailUrl}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        fileTypeFilter={fileTypeFilter}
        handleFileTypeFilterChange={handleFileTypeFilterChange}
        fileTypeOptions={fileTypeOptions}
        assetCategoryFilter={assetCategoryFilter}
        handleAssetCategoryFilterChange={handleAssetCategoryFilterChange}
        assetCategoryOptions={assetCategoryOptions}
        onRename={renameAsset}
        onDelete={deleteAsset}
        onOptimize={handleOptimizeAsset}
        onAddToLibrary={handleAddToLibrary}
        canAddToLibrary={canShowAddToLibrary}
        tabs={tabs}
        selectedTab={selectedTab}
        handleSelectedTabChange={handleSelectedTabChange}
        sourceOptions={sourceOptions}
        handleSourceChange={handleSourceChange}
        source={source}
        hasNext={hasNext}
        hasPrev={hasPrev}
        onNext={onNext}
        onPrev={onPrev}
        editable={editable && canUpdateAssets}
        canDeleteAssets={canDeleteAssets}
        onSelectAssets={onSelectAssets}
        placeLoading={placeLoading}
        onChangePlaceLoading={onChangePlaceLoading}
        userName={userName}
        sessionId={sessionId}
        initEditMode={initEditMode}
        openFileUploadDrawer={openFileUploadDrawer}
        onClearEdit={onClear}
        selectionDisabled={selectionDisabled}
      />
      <Optimize3DModelDialog
        asset={dialogAsset}
        open={isOptimizeDialogOpen}
        onClose={handleCloseOptimizeDialog}
        onOptimized={handleOptimizationComplete}
      />
    </>
  );
}

AssetsSidebarContainer.propTypes = {
  scene: PropTypes.object,
  initialTab: PropTypes.string,
  signedIn: PropTypes.bool,
  userName: PropTypes.string,
  sessionId: PropTypes.string
};
